import { StaticJsonRpcProvider } from '@ethersproject/providers';
import { useEthersAdaptorFromProviderOrSigners } from 'eth-hooks2';
import { EthersModalConnector, TEthersModalConnector, useEthersAppContext } from 'eth-hooks2/context';
import { useEffect } from 'react';
import { useThemeSwitcher } from 'react-css-theme-switcher';

import { useGetCreateLoginConnector } from '~common/components/hooks/useGetLoginConnector';
import { useGetWeb3ModalConfig } from '~common/components/hooks/useGetWeb3ModalConfig';
import { customWeb3ModalProviders } from '~common/config/web3Modal.config';
import { TNetworkDefinition } from '~common/constants';
import { IScaffoldAppProviders } from '~common/models/IScaffoldAppProviders';

export const useScaffoldAppProviders = (config: {
  mainnetProvider: StaticJsonRpcProvider | undefined;
  localProvider: StaticJsonRpcProvider | undefined;
  targetNetworks: { [chainId: number]: TNetworkDefinition };
  infuraId: string;
  connectToBurnerAutomatically: boolean;
}): IScaffoldAppProviders => {
  const ethersAppContext = useEthersAppContext();
  const [mainnetAdaptor] = useEthersAdaptorFromProviderOrSigners(config.mainnetProvider);
  const [localAdaptor] = useEthersAdaptorFromProviderOrSigners(config.localProvider);

  const hasLocalProvider = config?.localProvider !== undefined;
  const web3Config = useGetWeb3ModalConfig(hasLocalProvider, { infuraId: config.infuraId });

  const { currentTheme } = useThemeSwitcher();

  const createLoginConnector = useGetCreateLoginConnector(currentTheme, web3Config);

  useEffect(() => {
    /**
     * This is for to auto connect to the burner wallet when there is no cached provier
     * you can turn it off by settting {@link const_ConnectToBurnerOnFirstLoad} to false
     * @param connector
     * @returns
     */
    const autoConnectToBurner = (connector: TEthersModalConnector | undefined): TEthersModalConnector | undefined => {
      let newConnector = connector;
      if (config.connectToBurnerAutomatically && connector && connector?.loadWeb3Modal) {
        connector.loadWeb3Modal();
        if (connector != null && !connector.hasCachedProvider()) {
          newConnector = new EthersModalConnector(
            { ...web3Config, theme: currentTheme },
            { reloadOnNetworkChange: false, immutableProvider: false },
            customWeb3ModalProviders.localhostKey
          );
        }
      }
      return newConnector;
    };

    if (!ethersAppContext.active && createLoginConnector) {
      let connector = createLoginConnector(undefined);
      connector = autoConnectToBurner(connector);
      if (connector) void ethersAppContext.activate(connector);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [web3Config, config.connectToBurnerAutomatically, createLoginConnector]);

  const currentTargetNetwork =
    ethersAppContext.chainId != null && config.targetNetworks[ethersAppContext.chainId]
      ? config.targetNetworks[ethersAppContext.chainId]
      : Object.values(config.targetNetworks)[0];

  return {
    currentProvider: ethersAppContext.provider ?? config.localProvider,
    mainnetAdaptor: mainnetAdaptor,
    localAdaptor: localAdaptor,
    currentTargetNetwork: currentTargetNetwork,
    targetNetworks: config.targetNetworks,
    createLoginConnector: createLoginConnector,
  };
};
